import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles,
  Crown,
  Award,
  Flame,
  BookOpen,
  GraduationCap,
  Plus,
  Minus,
  X as XIcon,
  Divide,
  Check,
  Mail,
  Percent,
  PieChart,
  Sigma,
  ArrowRight,
  Star,
  ChevronDown,
  Zap,
  Heart,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const SUPPORT_EMAIL = "mariana.flores@mateconmar.com";

// --- shared motion variants ---
const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.5, ease: "easeOut" },
};

// --- Reusable Clay Button ---
function ClayCta({ children, onClick, variant = "primary", size = "md", testid }) {
  const sizes = {
    md: "text-sm sm:text-lg py-2.5 px-4 sm:py-3.5 sm:px-7",
    lg: "text-lg sm:text-xl py-4 px-7 sm:py-4 sm:px-9",
  };
  const variants = {
    primary:
      "bg-[#FF9500] text-white shadow-[0_6px_0_0_#B05A00] hover:shadow-[0_4px_0_0_#B05A00] hover:translate-y-[2px] active:shadow-[0_0_0_0_#B05A00] active:translate-y-[6px]",
    secondary:
      "bg-white text-[#1C1C1E] border-2 border-[#FFEEDC] shadow-[0_4px_0_0_#FFEEDC] hover:shadow-[0_2px_0_0_#FFEEDC] hover:translate-y-[2px]",
  };
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center justify-center gap-2 rounded-full font-bold transition-all ${sizes[size]} ${variants[variant]}`}
    >
      {children}
    </button>
  );
}

// --- Floating decorative icons ---
function FloatingIcon({ Icon, color, x, y, delay = 0, size = 56 }) {
  return (
    <motion.div
      aria-hidden="true"
      initial={{ opacity: 0, scale: 0 }}
      animate={{
        opacity: 1,
        scale: 1,
        y: [0, -14, 0],
      }}
      transition={{
        opacity: { duration: 0.4, delay },
        scale: { type: "spring", bounce: 0.5, delay },
        y: { repeat: Infinity, duration: 4 + delay, ease: "easeInOut" },
      }}
      style={{ left: x, top: y }}
      className="absolute pointer-events-none flex items-center justify-center rounded-2xl shadow-[0_6px_0_0_rgba(0,0,0,0.08)]"
    >
      <div
        className="flex items-center justify-center rounded-2xl"
        style={{
          width: size,
          height: size,
          background: color.bg,
          color: color.fg,
        }}
      >
        <Icon className="w-1/2 h-1/2" strokeWidth={3} />
      </div>
    </motion.div>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();

  const goSignup = () => navigate("/login?signup=1");
  const goLogin = () => navigate("/login");

  return (
    <div className="kid-bg min-h-screen overflow-x-hidden font-body">
      {/* Header */}
      <header
        className="sticky top-0 z-40 bg-[#FFF9E6]/85 backdrop-blur border-b border-[#FFEEDC]"
        data-testid="landing-header"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex items-center justify-between gap-3">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2"
            data-testid="landing-logo"
          >
            <span className="inline-flex w-9 h-9 sm:w-10 sm:h-10 rounded-2xl items-center justify-center bg-gradient-to-br from-[#FFD700] to-[#FF9500] shadow-[0_4px_0_0_#B05A00]">
              <Sparkles className="w-5 h-5 text-white" strokeWidth={3} />
            </span>
            <span className="font-heading text-xl sm:text-2xl font-bold text-[#1C1C1E]">
              MateConMar
            </span>
          </button>
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={goLogin}
              className="text-xs sm:text-base font-bold text-[#5C5C60] hover:text-[#1C1C1E] px-1.5 sm:px-3 py-2 transition-colors whitespace-nowrap"
              data-testid="landing-header-login-btn"
            >
              Ya tengo cuenta
            </button>
            <ClayCta onClick={goSignup} size="md" testid="landing-header-cta">
              <span className="hidden sm:inline">Empieza gratis</span>
              <span className="sm:hidden">Entrar</span>
              <ArrowRight className="w-4 h-4" strokeWidth={3} />
            </ClayCta>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section
        className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-20 pb-16 sm:pb-24"
        data-testid="landing-hero"
      >
        {/* Decorative floating icons (desktop only — they distract on small screens) */}
        <div className="hidden lg:block">
          <FloatingIcon Icon={Plus} color={{ bg: "#FFE5E3", fg: "#B22A22" }} x="6%" y="10%" delay={0.1} />
          <FloatingIcon Icon={Minus} color={{ bg: "#E0F0FF", fg: "#0056B3" }} x="92%" y="14%" delay={0.4} />
          <FloatingIcon Icon={XIcon} color={{ bg: "#DFF3FB", fg: "#1F7BA8" }} x="3%" y="62%" delay={0.6} size={48} />
          <FloatingIcon Icon={Divide} color={{ bg: "#FFF6D5", fg: "#A07800" }} x="94%" y="58%" delay={0.3} size={48} />
          <FloatingIcon Icon={Flame} color={{ bg: "#FFEEDC", fg: "#FF6B00" }} x="14%" y="78%" delay={0.7} size={44} />
          <FloatingIcon Icon={Star} color={{ bg: "#FFF6CC", fg: "#7A5C00" }} x="85%" y="80%" delay={0.5} size={44} />
        </div>

        <div className="relative text-center max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="inline-flex items-center gap-2 bg-white border-2 border-[#FFD700] rounded-full px-4 py-1.5 mb-6 shadow-[0_4px_0_0_#FFEEDC]"
          >
            <Sparkles className="w-4 h-4 text-[#FF9500]" strokeWidth={3} />
            <span className="text-xs sm:text-sm font-bold uppercase tracking-wider text-[#7A5C00]">
              7 días gratis, sin tarjeta
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.05 }}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-[#1C1C1E] leading-[1.05] tracking-tight"
          >
            Matemáticas{" "}
            <span className="relative inline-block">
              <span className="relative z-10 text-[#FF9500]">divertidas</span>
              <span className="absolute inset-x-1 bottom-1 sm:bottom-2 h-3 sm:h-4 bg-[#FFD700]/40 -z-0 rounded-full" />
            </span>
            <br />
            para tus hijos
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="mt-5 sm:mt-7 text-base sm:text-lg lg:text-xl text-[#5C5C60] max-w-2xl mx-auto leading-relaxed"
          >
            <strong className="text-[#1C1C1E]">MateConMar</strong> convierte la práctica de matemáticas
            en un juego: rondas cortas, medallas, rachas diarias y temas que crecen con tu hijo desde
            <span className="font-bold text-[#FF9500]"> primaria</span> hasta
            <span className="font-bold text-[#7B61FF]"> secundaria</span>.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.25 }}
            className="mt-8 sm:mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4"
          >
            <ClayCta onClick={goSignup} size="lg" testid="landing-hero-signup-btn">
              <Sparkles className="w-5 h-5" strokeWidth={3} />
              Empieza gratis 7 días
            </ClayCta>
            <ClayCta onClick={goLogin} variant="secondary" size="lg" testid="landing-hero-login-btn">
              Ya tengo cuenta
            </ClayCta>
          </motion.div>

          {/* Trust micro-bar */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-6 sm:mt-8 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs sm:text-sm text-[#5C5C60] font-bold"
          >
            <span className="inline-flex items-center gap-1.5">
              <Check className="w-4 h-4 text-[#34C759]" strokeWidth={3} />
              Sin tarjeta
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Check className="w-4 h-4 text-[#34C759]" strokeWidth={3} />
              Cancela cuando quieras
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Check className="w-4 h-4 text-[#34C759]" strokeWidth={3} />
              Hecho en México 🇲🇽
            </span>
          </motion.div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24"
        data-testid="landing-how-it-works"
      >
        <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-12 sm:mb-16">
          <p className="text-xs sm:text-sm font-bold uppercase tracking-wider text-[#7A5C00] mb-2">
            En 4 pasos
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1C1C1E] leading-tight">
            Empezar es muy fácil
          </h2>
          <p className="mt-3 text-base sm:text-lg text-[#5C5C60]">
            Sin descargas, sin papeleos. Lista en menos de un minuto.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
          {[
            {
              num: 1,
              title: "Crea tu cuenta",
              text: "Solo tu nombre, correo y una contraseña. 7 días gratis al instante, sin tarjeta.",
              color: "#FF9500",
              colorLight: "#FFEEDC",
              icon: Sparkles,
            },
            {
              num: 2,
              title: "Elige un tema",
              text: "Suma, resta, fracciones, álgebra… y nivel fácil, medio o difícil.",
              color: "#007AFF",
              colorLight: "#E0F0FF",
              icon: BookOpen,
            },
            {
              num: 3,
              title: "Juega una ronda",
              text: "10 preguntas con opción múltiple. Feedback inmediato y simpático.",
              color: "#34C759",
              colorLight: "#E8F9EE",
              icon: Zap,
            },
            {
              num: 4,
              title: "Gana medallas",
              text: "Acumula puntos, desbloquea logros y mantén la racha cada día.",
              color: "#FFD700",
              colorLight: "#FFF6CC",
              icon: Award,
            },
          ].map((step, i) => (
            <motion.div
              key={step.num}
              {...fadeUp}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="bg-white rounded-3xl border-2 border-[#FFEEDC] shadow-[0_8px_0_0_#FFEEDC] p-5 sm:p-6 relative hover:-translate-y-1 hover:shadow-[0_10px_0_0_#FFEEDC] transition-all"
              data-testid={`how-step-${step.num}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div
                  className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center"
                  style={{ background: step.colorLight, color: step.color }}
                >
                  <step.icon className="w-7 h-7 sm:w-8 sm:h-8" strokeWidth={2.5} />
                </div>
                <span
                  className="font-heading text-3xl sm:text-4xl font-bold opacity-20"
                  style={{ color: step.color }}
                >
                  {step.num}
                </span>
              </div>
              <h3 className="font-heading text-lg sm:text-xl font-bold text-[#1C1C1E] mb-1">
                {step.title}
              </h3>
              <p className="text-sm sm:text-base text-[#5C5C60] leading-relaxed">{step.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* OPERATIONS / TOPICS */}
      <section
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24"
        data-testid="landing-operations"
      >
        <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-10 sm:mb-14">
          <p className="text-xs sm:text-sm font-bold uppercase tracking-wider text-[#7A5C00] mb-2">
            Contenido educativo
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1C1C1E] leading-tight">
            Todo lo que tu hijo necesita
          </h2>
          <p className="mt-3 text-base sm:text-lg text-[#5C5C60]">
            Plan de estudios alineado al sistema mexicano. Decimales con punto, como en la escuela.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6">
          {/* PRIMARIA */}
          <motion.div
            {...fadeUp}
            className="bg-gradient-to-br from-[#FFEEDC] to-white rounded-[2rem] border-2 border-[#FFEEDC] shadow-[0_10px_0_0_#FFEEDC] p-6 sm:p-8"
            data-testid="operations-primaria"
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-[#FF9500] flex items-center justify-center shadow-[0_4px_0_0_#B05A00]">
                <BookOpen className="w-7 h-7 sm:w-8 sm:h-8 text-white" strokeWidth={2.5} />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-[#B05A00]">
                  Nivel
                </p>
                <h3 className="font-heading text-2xl sm:text-3xl font-bold text-[#1C1C1E]">
                  Primaria
                </h3>
              </div>
            </div>
            <p className="text-sm sm:text-base text-[#5C5C60] mb-5">
              7 operaciones con 3 niveles cada una: fácil, medio y difícil.
            </p>
            <div className="flex flex-wrap gap-2">
              {[
                { name: "Suma", icon: Plus, color: "#FF3B30", bg: "#FFE5E3" },
                { name: "Resta", icon: Minus, color: "#007AFF", bg: "#E0F0FF" },
                { name: "Multiplicación", icon: XIcon, color: "#32ADE6", bg: "#DFF3FB" },
                { name: "División", icon: Divide, color: "#E0A800", bg: "#FFF6D5" },
                { name: "Porcentajes", icon: Percent, color: "#AF52DE", bg: "#F1E2FA" },
                { name: "Fracciones", icon: PieChart, color: "#EC4899", bg: "#FCE7F3" },
                { name: "Decimales", icon: Sparkles, color: "#10B981", bg: "#D1FAE5" },
              ].map((op) => (
                <span
                  key={op.name}
                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 font-bold text-sm"
                  style={{ background: op.bg, color: op.color }}
                  data-testid={`op-primaria-${op.name.toLowerCase()}`}
                >
                  <op.icon className="w-4 h-4" strokeWidth={3} />
                  {op.name}
                </span>
              ))}
            </div>
          </motion.div>

          {/* SECUNDARIA */}
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-gradient-to-br from-[#E9E3FF] to-white rounded-[2rem] border-2 border-[#E9E3FF] shadow-[0_10px_0_0_#E9E3FF] p-6 sm:p-8"
            data-testid="operations-secundaria"
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-[#7B61FF] flex items-center justify-center shadow-[0_4px_0_0_#3D2EA0]">
                <GraduationCap className="w-7 h-7 sm:w-8 sm:h-8 text-white" strokeWidth={2.5} />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-[#3D2EA0]">Nivel</p>
                <h3 className="font-heading text-2xl sm:text-3xl font-bold text-[#1C1C1E]">
                  Secundaria
                </h3>
              </div>
            </div>
            <p className="text-sm sm:text-base text-[#5C5C60] mb-5">
              Álgebra básica con 4 tipos de ejercicios. Más temas en camino.
            </p>
            <div className="flex flex-wrap gap-2 mb-4">
              {[
                "Ecuaciones de 1er grado",
                "Suma de polinomios",
                "Resta de polinomios",
                "Multiplicación de monomios",
              ].map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 font-bold text-sm bg-[#E9E3FF] text-[#3D2EA0]"
                >
                  <Sigma className="w-4 h-4" strokeWidth={3} />
                  {t}
                </span>
              ))}
            </div>
            <p className="text-xs font-bold uppercase tracking-wider text-[#5C5C60] mb-2">
              Próximamente
            </p>
            <div className="flex flex-wrap gap-2">
              {["Geometría", "Potencias y raíces", "Regla de tres"].map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center rounded-full px-3 py-1 font-bold text-xs bg-white text-gray-400 border-2 border-gray-100"
                >
                  {t}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* GAMIFICATION */}
      <section
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24"
        data-testid="landing-gamification"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <motion.div {...fadeUp}>
            <p className="text-xs sm:text-sm font-bold uppercase tracking-wider text-[#7A5C00] mb-2">
              Aprender se siente bien
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1C1C1E] leading-tight">
              Medallas, rachas y puntos que motivan
            </h2>
            <p className="mt-4 text-base sm:text-lg text-[#5C5C60] leading-relaxed">
              Cada acierto suma puntos. Cada día de práctica enciende la racha. Y cada nuevo logro se
              guarda en su colección de medallas. La motivación deja de ser un problema.
            </p>
            <ul className="mt-6 space-y-3">
              {[
                { Icon: Award, color: "#FFD700", text: "18 medallas: oro, plata y bronce" },
                { Icon: Flame, color: "#FF6B00", text: "Racha diaria que se reinicia si no juega" },
                { Icon: Star, color: "#FF9500", text: "Puntos por nivel: 5, 10 o 20 por acierto" },
                { Icon: Heart, color: "#FF3B30", text: "Mensajes amables incluso cuando se equivoca" },
              ].map((item) => (
                <li key={item.text} className="flex items-start gap-3">
                  <span
                    className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                    style={{ background: "#FFF6CC" }}
                  >
                    <item.Icon className="w-5 h-5" strokeWidth={2.5} style={{ color: item.color }} />
                  </span>
                  <span className="text-base text-[#1C1C1E] font-medium pt-1.5">{item.text}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Visual mock */}
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="relative"
          >
            <div className="relative bg-white rounded-[2.5rem] border-2 border-[#FFEEDC] shadow-[0_10px_0_0_#FFEEDC] p-6 sm:p-8">
              {/* Streak banner */}
              <div className="flex items-center gap-3 bg-[#FFEEDC] border-2 border-[#FF9500] rounded-2xl px-4 py-3 mb-4">
                <div className="w-12 h-12 rounded-2xl bg-[#FF9500] flex items-center justify-center shadow-[0_3px_0_0_#B05A00]">
                  <Flame className="w-6 h-6 text-white" strokeWidth={2.5} fill="white" />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-[#B05A00]">
                    Racha actual
                  </p>
                  <p className="font-heading text-2xl font-bold text-[#1C1C1E]">
                    5 días 🔥
                  </p>
                </div>
              </div>

              {/* Stats grid */}
              <div className="grid grid-cols-3 gap-2 mb-4">
                {[
                  { label: "Aciertos", value: "127", bg: "#E8F9EE", fg: "#1A6B2F" },
                  { label: "Puntos", value: "1,840", bg: "#FFF6CC", fg: "#7A5C00" },
                  { label: "Medallas", value: "9/18", bg: "#FFEEDC", fg: "#B05A00" },
                ].map((s) => (
                  <div
                    key={s.label}
                    className="rounded-2xl p-3 text-center"
                    style={{ background: s.bg }}
                  >
                    <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider" style={{ color: s.fg }}>
                      {s.label}
                    </p>
                    <p
                      className="font-heading text-xl sm:text-2xl font-bold"
                      style={{ color: s.fg }}
                    >
                      {s.value}
                    </p>
                  </div>
                ))}
              </div>

              {/* Medals row */}
              <p className="text-xs font-bold uppercase tracking-wider text-[#5C5C60] mb-2">
                Medallas recientes
              </p>
              <div className="flex gap-2 flex-wrap">
                {[
                  { name: "Primer Paso", color: "#CD7F32", bg: "#FFE5DC" },
                  { name: "Ronda Perfecta", color: "#FFD700", bg: "#FFF6CC" },
                  { name: "Racha de 5", color: "#C0C0C0", bg: "#F1F1F4" },
                  { name: "Maestro de Sumas", color: "#C0C0C0", bg: "#F1F1F4" },
                ].map((m) => (
                  <div
                    key={m.name}
                    className="flex-1 min-w-[110px] rounded-2xl p-2.5 text-center border-2"
                    style={{ background: m.bg, borderColor: m.color }}
                  >
                    <Award className="w-6 h-6 mx-auto" strokeWidth={2.5} style={{ color: m.color }} />
                    <p className="text-[10px] font-bold mt-1 text-[#1C1C1E] leading-tight">
                      {m.name}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Floating decoration */}
            <motion.div
              animate={{ y: [0, -10, 0], rotate: [-3, 3, -3] }}
              transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
              className="hidden sm:flex absolute -top-6 -right-6 w-16 h-16 rounded-3xl items-center justify-center bg-gradient-to-br from-[#FFD700] to-[#FF9500] shadow-[0_6px_0_0_#B05A00]"
              aria-hidden="true"
            >
              <Crown className="w-9 h-9 text-white" strokeWidth={2.5} />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* PRICING */}
      <section
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24"
        data-testid="landing-pricing"
      >
        <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-10 sm:mb-14">
          <p className="text-xs sm:text-sm font-bold uppercase tracking-wider text-[#7A5C00] mb-2">
            Precios claros
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1C1C1E] leading-tight">
            Empieza gratis. Sigue solo si te encanta.
          </h2>
          <p className="mt-3 text-base sm:text-lg text-[#5C5C60]">
            Prueba <strong>7 días Premium gratis</strong> sin tarjeta. Después, decide.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6 max-w-5xl mx-auto">
          {/* FREE */}
          <motion.div
            {...fadeUp}
            className="bg-white rounded-[2rem] border-2 border-[#FFEEDC] shadow-[0_8px_0_0_#FFEEDC] p-6 sm:p-8 flex flex-col"
            data-testid="pricing-free"
          >
            <p className="text-sm font-bold uppercase tracking-wider text-[#5C5C60]">Gratis</p>
            <p className="mt-2 font-heading text-4xl sm:text-5xl font-bold text-[#1C1C1E]">
              $0
            </p>
            <p className="text-sm text-[#5C5C60] mt-1">Siempre</p>
            <ul className="mt-6 space-y-2.5 text-sm text-[#1C1C1E] flex-1">
              {[
                "1 ronda al día en Primaria",
                "Sistema de medallas y rachas",
                "Sin Secundaria",
                "Para probar antes de decidir",
              ].map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#34C759] shrink-0 mt-1" strokeWidth={3} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* MONTHLY (Recommended) */}
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-gradient-to-br from-[#FFF6CC] to-white rounded-[2rem] border-4 border-[#FFD700] shadow-[0_12px_0_0_#FFE56B] p-6 sm:p-8 relative flex flex-col -mt-2 md:-mt-4"
            data-testid="pricing-monthly"
          >
            <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#FF9500] text-white text-xs font-bold uppercase tracking-wider rounded-full px-3 py-1 shadow-[0_3px_0_0_#B05A00]">
              7 días gratis
            </span>
            <p className="text-sm font-bold uppercase tracking-wider text-[#B05A00]">Premium · Mensual</p>
            <p className="mt-2 font-heading text-4xl sm:text-5xl font-bold text-[#1C1C1E]">
              $99
              <span className="text-base sm:text-lg text-[#5C5C60] font-bold ml-1">MXN/mes</span>
            </p>
            <p className="text-sm text-[#5C5C60] mt-1">Pago único, sin renovación auto</p>
            <ul className="mt-6 space-y-2.5 text-sm text-[#1C1C1E] flex-1">
              {[
                "Rondas ilimitadas en Primaria",
                "Acceso completo a Secundaria",
                "Todas las medallas y rachas",
                "Nuevos temas conforme se agreguen",
                "7 días de reembolso garantizado",
              ].map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#34C759] shrink-0 mt-1" strokeWidth={3} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={goSignup}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#FF9500] text-white rounded-full font-bold text-base sm:text-lg py-3.5 shadow-[0_6px_0_0_#B05A00] hover:shadow-[0_4px_0_0_#B05A00] hover:translate-y-[2px] transition-all"
              data-testid="pricing-monthly-cta"
            >
              <Sparkles className="w-5 h-5" strokeWidth={3} />
              Empieza gratis 7 días
            </button>
          </motion.div>

          {/* YEARLY */}
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-white rounded-[2rem] border-2 border-[#FFEEDC] shadow-[0_8px_0_0_#FFEEDC] p-6 sm:p-8 relative flex flex-col"
            data-testid="pricing-yearly"
          >
            <span className="absolute -top-3 right-5 bg-[#34C759] text-white text-xs font-bold uppercase tracking-wider rounded-full px-3 py-1 shadow-[0_3px_0_0_#1A6B2F]">
              Ahorras 33%
            </span>
            <p className="text-sm font-bold uppercase tracking-wider text-[#5C5C60]">Premium · Anual</p>
            <p className="mt-2 font-heading text-4xl sm:text-5xl font-bold text-[#1C1C1E]">
              $799
              <span className="text-base sm:text-lg text-[#5C5C60] font-bold ml-1">MXN/año</span>
            </p>
            <p className="text-sm text-[#1A6B2F] font-bold mt-1">≈ $66.6 MXN/mes</p>
            <ul className="mt-6 space-y-2.5 text-sm text-[#1C1C1E] flex-1">
              {[
                "Todo lo de Premium Mensual",
                "Equivale a 8 meses pagando 12",
                "Pago único, sin sorpresas",
                "365 días de acceso completo",
              ].map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#34C759] shrink-0 mt-1" strokeWidth={3} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={goSignup}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-white border-2 border-[#FFEEDC] text-[#1C1C1E] rounded-full font-bold text-base sm:text-lg py-3.5 shadow-[0_5px_0_0_#FFEEDC] hover:shadow-[0_3px_0_0_#FFEEDC] hover:translate-y-[2px] transition-all"
              data-testid="pricing-yearly-cta"
            >
              Elegir anual
            </button>
          </motion.div>
        </div>

        <p className="text-center text-xs text-[#5C5C60] mt-6 max-w-xl mx-auto">
          Pagos seguros vía Stripe. Pago único — no se renueva automáticamente. Sin sorpresas.
        </p>
      </section>

      {/* FAQ */}
      <section
        className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24"
        data-testid="landing-faq"
      >
        <motion.div {...fadeUp} className="text-center mb-10 sm:mb-12">
          <p className="text-xs sm:text-sm font-bold uppercase tracking-wider text-[#7A5C00] mb-2">
            Preguntas frecuentes
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1C1C1E]">
            Resolvemos tus dudas
          </h2>
        </motion.div>

        <motion.div {...fadeUp}>
          <Accordion type="single" collapsible className="bg-white rounded-3xl border-2 border-[#FFEEDC] shadow-[0_8px_0_0_#FFEEDC] px-4 sm:px-6">
            {[
              {
                q: "¿Necesito tarjeta para la prueba gratis de 7 días?",
                a: "No. Los 7 días son completamente gratis y no piden ningún dato de pago. Al terminar, si decides no suscribirte, simplemente pasas al plan gratis (1 ronda al día). No hay cargos sorpresa.",
              },
              {
                q: "¿Para qué edad es la app?",
                a: "MateConMar está diseñada para niños y adolescentes de aproximadamente 6 a 15 años. Cubre desde sumas básicas de primaria hasta álgebra de secundaria, con 3 niveles de dificultad para cada tema.",
              },
              {
                q: "¿Puedo usar la app sin registrarme?",
                a: "Sí. Existe el modo invitado: tu hijo puede practicar 1 ronda al día sin crear cuenta. El progreso se guarda solo en ese dispositivo. Crear cuenta es gratis y desbloquea los 7 días Premium + sincronización en la nube.",
              },
              {
                q: "¿Cómo cancelo mi suscripción?",
                a: "No hay nada que cancelar. Nuestros planes son de pago único — no se renuevan automáticamente. Cuando termine el periodo que pagaste (30 o 365 días), tu cuenta vuelve al plan gratis hasta que decidas renovar.",
              },
              {
                q: "¿Puedo pedir reembolso si no me gusta?",
                a: "Sí. Tienes 7 días desde el pago para solicitar un reembolso completo, sin preguntas. Solo escríbenos a " + SUPPORT_EMAIL + " con tu usuario y nosotros nos encargamos.",
              },
              {
                q: "¿En qué dispositivos funciona?",
                a: "Funciona en cualquier navegador moderno: computadora, tablet o celular (iPhone o Android). No requiere instalación ni descargas. Solo entra a la app y empieza.",
              },
              {
                q: "¿Funciona sin internet?",
                a: "Necesita conexión a internet para guardar el progreso en tu cuenta. Si pierdes señal a media ronda, podrás terminarla, pero requiere internet para iniciar.",
              },
              {
                q: "¿Los decimales se ven con punto o coma?",
                a: "Con punto, como se usa en México (ejemplo: 3.5, no 3,5). Toda la app está pensada para alumnos mexicanos.",
              },
            ].map((item, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="border-b border-[#FFEEDC] last:border-b-0"
                data-testid={`faq-item-${i}`}
              >
                <AccordionTrigger className="text-left font-heading text-base sm:text-lg font-bold text-[#1C1C1E] hover:no-underline py-4 sm:py-5">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm sm:text-base text-[#5C5C60] leading-relaxed pb-4 sm:pb-5">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 sm:pb-16">
        <motion.div
          {...fadeUp}
          className="relative bg-gradient-to-br from-[#FF9500] to-[#FFD700] rounded-[2.5rem] p-8 sm:p-12 text-center overflow-hidden shadow-[0_12px_0_0_#B05A00]"
        >
          <div className="absolute inset-0 opacity-20 pointer-events-none">
            <div className="absolute top-6 left-8 text-white text-6xl font-bold rotate-[-12deg]">+</div>
            <div className="absolute bottom-6 right-10 text-white text-7xl font-bold rotate-[15deg]">×</div>
            <div className="absolute top-10 right-12 text-white text-5xl font-bold">÷</div>
          </div>
          <div className="relative">
            <h3 className="font-heading text-2xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
              ¿Listos para hacer las mates divertidas?
            </h3>
            <p className="mt-3 text-base sm:text-lg text-white/95 max-w-xl mx-auto">
              7 días Premium gratis. Sin tarjeta. Cancela cuando quieras.
            </p>
            <button
              onClick={goSignup}
              className="mt-7 inline-flex items-center justify-center gap-2 bg-white text-[#FF9500] rounded-full font-bold text-base sm:text-lg py-3.5 px-7 sm:px-9 shadow-[0_6px_0_0_rgba(0,0,0,0.15)] hover:shadow-[0_4px_0_0_rgba(0,0,0,0.15)] hover:translate-y-[2px] active:shadow-[0_0_0_0_rgba(0,0,0,0.15)] active:translate-y-[6px] transition-all"
              data-testid="landing-final-cta"
            >
              <Sparkles className="w-5 h-5" strokeWidth={3} />
              Empezar mi prueba gratis
              <ArrowRight className="w-5 h-5" strokeWidth={3} />
            </button>
          </div>
        </motion.div>
      </section>

      {/* FOOTER */}
      <footer
        className="border-t-2 border-[#FFEEDC] bg-[#FFF9E6]"
        data-testid="landing-footer"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span className="inline-flex w-8 h-8 rounded-xl items-center justify-center bg-gradient-to-br from-[#FFD700] to-[#FF9500]">
              <Sparkles className="w-4 h-4 text-white" strokeWidth={3} />
            </span>
            <span className="font-heading text-lg font-bold text-[#1C1C1E]">MateConMar</span>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm text-[#5C5C60] font-bold">
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="inline-flex items-center gap-1.5 hover:text-[#1C1C1E] transition-colors"
              data-testid="footer-contact"
            >
              <Mail className="w-4 h-4" strokeWidth={2.5} />
              Contacto
            </a>
            <button
              onClick={() => navigate("/terminos")}
              className="hover:text-[#1C1C1E] transition-colors"
              data-testid="footer-terms"
            >
              Términos y Reembolso
            </button>
            <span className="text-xs text-[#5C5C60]">© 2026 MateConMar</span>
          </div>
        </div>
      </footer>

      {/* lucide-react ChevronDown is needed for the Accordion */}
      {false && <ChevronDown />}

      {/* Bottom cream band is provided globally by body::after in App.css */}
    </div>
  );
}
